import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Activity,
  Archive,
  Calendar,
  BarChart3,
  Landmark,
  Shield,
  CheckCircle,
  XCircle,
  Layers,
  Gavel,
  FileSpreadsheet,
  FileText,
  AlertCircle,
  RefreshCw,
  Filter,
  Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { aggregate, formatCurrency } from '../utils/grouping';
import IconButton from '../components/IconButton';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [consolidacoes, setConsolidacoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [faixas, setFaixas] = useState([]);
  const [regras, setRegras] = useState([]);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [
        movRes,
        consRes,
        cliRes,
        grpRes,
        faiRes,
        regRes
      ] = await Promise.allSettled([
        api.get('/movimentacao').catch(() => ({ data: { data: [] } })),
        api.get('/consolidacao').catch(() => ({ data: { data: [] } })),
        api.get('/cliente').catch(() => ({ data: { data: [] } })),
        api.get('/gruposegur').catch(() => ({ data: { data: [] } })),
        api.get('/faixas').catch(() => ({ data: { data: [] } })),
        api.get('/regras').catch(() => ({ data: { data: [] } }))
      ]);

      const unwrap = (res) => res.status === 'fulfilled' ? (res.value?.data?.data || []) : [];

      setMovimentacoes(unwrap(movRes));
      setConsolidacoes(unwrap(consRes));
      setClientes(unwrap(cliRes));
      setGrupos(unwrap(grpRes));
      setFaixas(unwrap(faiRes));
      setRegras(unwrap(regRes));
    } catch (err) {
      console.error('Erro ao carregar dados do dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const getEmpresaNome = (item) => {
    if (!item) return '-';
    const cli = clientes.find(c =>
      String(c?.cd_supera_cli) === String(item?.cd_empresa_mov) ||
      String(c?.nome_cli) === String(item?.empresa_mov)
    );
    return cli?.nome_cli || item?.empresa_mov || 'Sem Empresa';
  };

  const getGrupoDesc = (item) => {
    if (!item) return '-';
    if (item.desc_grupo_mov) return item.desc_grupo_mov;
    const g = grupos.find(g => String(g?.id_grupo) === String(item?.id_grupo_mov));
    return g?.desc_grupo || '-';
  };

  const getFaixaDesc = (item) => {
    const f = faixas.find(x => String(x?.id) === String(item?.id_faixa_mov));
    return f?.desc_faixa || (item?.id_faixa_mov ?? '-');
  };

  const calcAge = (isoString) => {
    if (!isoString) return null;
    let d;
    if (String(isoString).includes('T')) {
      d = new Date(isoString);
    } else {
      const parts = String(isoString).split('-');
      if (parts.length !== 3) return null;
      d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
    if (Number.isNaN(d.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
    return age >= 0 ? age : null;
  };

  const formatBRL = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return formatCurrency(0);
    if (typeof value === 'number') return formatCurrency(value);
    try {
      const s = String(value).trim();
      const clean = s.replace(/[^0-9,.-]/g, '');
      const parsed = clean.includes(',')
        ? parseFloat(clean.replace(/\./g, '').replace(',', '.'))
        : parseFloat(clean);
      return Number.isNaN(parsed) ? formatCurrency(0) : formatCurrency(parsed);
    } catch (_) {
      return formatCurrency(0);
    }
  };

  const getFaixaValor = (item) => {
    const f = faixas.find(x => String(x?.id) === String(item?.id_faixa_mov));
    const raw = f?.valor_faixa;
    if (typeof raw === 'number') return raw;
    if (!raw) return 0;
    const clean = String(raw).replace(/[^0-9,.-]/g, '');
    const parsed = clean.includes(',')
      ? parseFloat(clean.replace(/\./g, '').replace(',', '.'))
      : parseFloat(clean);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getVlUnit = (item) => {
    if (item?.unit_mov) {
      const s = String(item.unit_mov).replace(',', '.');
      const p = parseFloat(s);
      return Number.isNaN(p) ? 0 : p;
    }
    const ageStr = calcAge(item?.dt_nasc_mov);
    if (ageStr === null || ageStr === '-') return 0;
    const age = parseInt(ageStr, 10);
    if (Number.isNaN(age)) return 0;
    const rule = regras.find(r => {
      const start = r.idade_inicial_regra !== null ? r.idade_inicial_regra : -Infinity;
      const end = r.idade_final_regra !== null ? r.idade_final_regra : Infinity;
      return age >= start && age <= end;
    });
    if (!rule) return 0;
    const taxaVal = parseFloat(String(rule.taxa || '0').replace(',', '.')) || 0;
    const fVal = getFaixaValor(item);
    if (!fVal) return 0;
    return Math.ceil((taxaVal * fVal / 100) * 100) / 100;
  };

  const movimentacoesAtivas = movimentacoes.filter(m => m.id_ativo === 1);
  const movimentacoesInativas = movimentacoes.filter(m => m.id_ativo !== 1);

  const consolidacaoAgg = aggregate(consolidacoes, {
    valor_mov: 'sum',
    unit_mov: 'sum'
  });
  const totalConsolidado = consolidacaoAgg.valor_mov || 0;
  const totalUnitario = consolidacaoAgg.unit_mov || 0;

  const totalUnitarioCalculado = consolidacoes.reduce((sum, item) => sum + (getVlUnit(item) || 0), 0);

  const totalCapitalSegurado = consolidacoes.reduce((sum, item) => sum + (getFaixaValor(item) || 0), 0);

  const countBy = (arr, getKey) => {
    const map = new Map();
    arr.forEach(item => {
      const k = String(getKey(item) ?? 'N/D');
      map.set(k, (map.get(k) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count);
  };

  const sumBy = (arr, getKey, getValue) => {
    const map = new Map();
    arr.forEach(item => {
      const k = String(getKey(item) ?? 'N/D');
      const v = getValue(item) || 0;
      map.set(k, (map.get(k) || 0) + v);
    });
    return Array.from(map.entries())
      .map(([key, total]) => ({ key, total }))
      .sort((a, b) => b.total - a.total);
  };

  const movsPorEmpresa = countBy(movimentacoes, m => getEmpresaNome(m)).slice(0, 6);
  const consPorEmpresa = sumBy(consolidacoes, c => getEmpresaNome(c), c => getVlUnit(c)).slice(0, 6);
  const movsPorGrupo = countBy(movimentacoes, m => getGrupoDesc(m)).slice(0, 5);
  const consPorMes = countBy(consolidacoes, c => c.mesano || 'Sem Mês').slice(0, 6);
  const consPorFaixa = countBy(consolidacoes, c => getFaixaDesc(c)).slice(0, 6);

  const CardMetric = ({ icon: Icon, title, value, subtitle, color = 'blue', onClick, trend }) => {
    const colorClasses = {
      blue: { iconBg: 'bg-blue-100 text-blue-600', border: 'border-blue-100', hover: 'hover:border-blue-300' },
      green: { iconBg: 'bg-green-100 text-green-600', border: 'border-green-100', hover: 'hover:border-green-300' },
      purple: { iconBg: 'bg-purple-100 text-purple-600', border: 'border-purple-100', hover: 'hover:border-purple-300' },
      orange: { iconBg: 'bg-orange-100 text-orange-600', border: 'border-orange-100', hover: 'hover:border-orange-300' },
      red: { iconBg: 'bg-red-100 text-red-600', border: 'border-red-100', hover: 'hover:border-red-300' },
      cyan: { iconBg: 'bg-cyan-100 text-cyan-600', border: 'border-cyan-100', hover: 'hover:border-cyan-300' },
      indigo: { iconBg: 'bg-indigo-100 text-indigo-600', border: 'border-indigo-100', hover: 'hover:border-indigo-300' },
      teal: { iconBg: 'bg-teal-100 text-teal-600', border: 'border-teal-100', hover: 'hover:border-teal-300' },
    };
    const cc = colorClasses[color] || colorClasses.blue;
    return (
      <div
        onClick={onClick}
        className={`bg-white rounded-xl shadow-sm border ${cc.border} ${cc.hover} p-5 transition-all duration-200 ${onClick ? 'cursor-pointer' : ''}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{title}</p>
            <p className="text-2xl font-bold text-gray-900 truncate">{value}</p>
            {subtitle && (
              <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
            )}
            {trend && (
              <div className={`text-xs font-medium mt-1 flex items-center gap-1 ${trend.positive ? 'text-green-600' : 'text-red-600'}`}>
                {trend.positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {trend.label}
              </div>
            )}
          </div>
          <div className={`${cc.iconBg} p-3 rounded-xl ml-3 flex-shrink-0`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </div>
    );
  };

  const MiniBar = ({ items, valueKey = 'count', valueLabel = 'itens', formatValue }) => {
    if (!items.length) {
      return (
        <div className="text-center py-6 text-gray-400 text-sm">
          Sem dados disponíveis
        </div>
      );
    }
    const maxVal = Math.max(...items.map(i => i[valueKey] || 0), 1);
    return (
      <div className="space-y-2">
        {items.map((item, idx) => {
          const val = item[valueKey] || 0;
          const pct = (val / maxVal) * 100;
          const display = formatValue ? formatValue(val) : `${val} ${valueLabel}`;
          return (
            <div key={idx}>
              <div className="flex justify-between items-center text-xs mb-1">
                <span className="font-medium text-gray-700 truncate pr-2 max-w-[60%]" title={item.key}>{item.key}</span>
                <span className="text-gray-500 flex-shrink-0 font-semibold">{display}</span>
              </div>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const SectionHeader = ({ title, subtitle, action }) => (
    <div className="flex items-center justify-between mb-4">
      <div>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Olá, {user?.name}! 👋
          </h1>
          <p className="text-gray-500 mt-1">Acompanhe os principais indicadores de movimentações e consolidações</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <IconButton
            icon={RefreshCw}
            onClick={carregarDados}
            variant="primary"
            title="Atualizar"
          />
          <div className="flex items-center text-sm text-gray-500 bg-white px-4 py-2 rounded-lg border border-gray-200">
            <Calendar className="w-4 h-4 mr-2" />
            {new Date().toLocaleDateString('pt-BR', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <CardMetric
          icon={Activity}
          title="Movimentações Total"
          value={movimentacoes.length.toLocaleString('pt-BR')}
          subtitle={`${movimentacoesAtivas.length} ativas · ${movimentacoesInativas.length} inativas`}
          color="blue"
          onClick={() => navigate('/movimentacao')}
        />
        <CardMetric
          icon={CheckCircle}
          title="Movimentações Ativas"
          value={movimentacoesAtivas.length.toLocaleString('pt-BR')}
          subtitle={movimentacoes.length ? `${Math.round((movimentacoesAtivas.length / movimentacoes.length) * 100)}% do total` : '0%'}
          color="green"
          onClick={() => navigate('/movimentacao')}
        />
        <CardMetric
          icon={XCircle}
          title="Movimentações Inativas"
          value={movimentacoesInativas.length.toLocaleString('pt-BR')}
          subtitle={movimentacoes.length ? `${Math.round((movimentacoesInativas.length / movimentacoes.length) * 100)}% do total` : '0%'}
          color="red"
        />
        <CardMetric
          icon={Users}
          title="Empresas (Clientes)"
          value={clientes.length.toLocaleString('pt-BR')}
          subtitle={`${new Set(movimentacoes.map(m => getEmpresaNome(m)).filter(e => e !== 'Sem Empresa')).size} com movimentações`}
          color="purple"
          onClick={() => navigate('/cliente')}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        <CardMetric
          icon={Archive}
          title="Consolidações Total"
          value={consolidacoes.length.toLocaleString('pt-BR')}
          subtitle="Registros consolidados"
          color="indigo"
          onClick={() => navigate('/consolidacao')}
        />
        <CardMetric
          icon={Landmark}
          title="Capital Segurado"
          value={formatBRL(totalCapitalSegurado)}
          subtitle="Soma dos valores de faixa"
          color="cyan"
        />
        <CardMetric
          icon={DollarSign}
          title="Valor Total Unitário"
          value={formatBRL(totalUnitarioCalculado)}
          subtitle="Premiação por idade + regra + faixa"
          color="teal"
        />
        <CardMetric
          icon={FileText}
          title="Consolidações Únicas"
          value={new Set(consolidacoes.map(c => c.nome_cons).filter(Boolean)).size.toLocaleString('pt-BR')}
          subtitle="Grupos de consolidação distintos"
          color="orange"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:col-span-2 gap-5">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <SectionHeader
              title="Movimentações por Empresa"
              subtitle="Distribuição das movimentações por seguradora"
              action={
                <IconButton
                  icon={Eye}
                  onClick={() => navigate('/movimentacao')}
                  variant="info"
                  size="sm"
                  title="Ver detalhes de Movimentações"
                />
              }
            />
            <MiniBar items={movsPorEmpresa} valueKey="count" valueLabel="mov." />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <SectionHeader
              title="Consolidações por Empresa"
              subtitle="Soma de valor unitário por seguradora"
              action={
                <IconButton
                  icon={Eye}
                  onClick={() => navigate('/consolidacao')}
                  variant="info"
                  size="sm"
                  title="Ver detalhes de Consolidações"
                />
              }
            />
            <MiniBar
              items={consPorEmpresa}
              valueKey="total"
              formatValue={(v) => formatBRL(v)}
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <SectionHeader
              title="Movimentações por Grupo"
              subtitle="Distribuição por grupo segurado"
              action={
                <IconButton
                  icon={Shield}
                  onClick={() => navigate('/gruposegur')}
                  variant="success"
                  size="sm"
                  title="Ver Grupos"
                />
              }
            />
            <MiniBar items={movsPorGrupo} valueKey="count" valueLabel="mov." />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <SectionHeader
              title="Consolidações por Mês/Ano"
              subtitle="Registros consolidados por período"
              action={
                <IconButton
                  icon={Filter}
                  onClick={() => navigate('/consolidacao')}
                  variant="primary"
                  size="sm"
                  title="Filtrar Consolidações"
                />
              }
            />
            <MiniBar items={consPorMes} valueKey="count" valueLabel="regs." />
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <SectionHeader
              title="Cadastros do Sistema"
              subtitle="Visão geral dos registros"
            />
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                    <Layers className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Faixas</p>
                    <p className="text-xs text-gray-500">Classes de capital</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-gray-900">{faixas.length}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Grupos Segur</p>
                    <p className="text-xs text-gray-500">Categorias de grupo</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-gray-900">{grupos.length}</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
                    <Gavel className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Regras</p>
                    <p className="text-xs text-gray-500">Faixas etárias e taxas</p>
                  </div>
                </div>
                <span className="text-lg font-bold text-gray-900">{regras.length}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <SectionHeader
              title="Consolidações por Faixa"
              subtitle="Quantidade por classe"
            />
            <MiniBar items={consPorFaixa} valueKey="count" valueLabel="regs." />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <SectionHeader
          title="Ações Rápidas"
          subtitle="Atalhos para as principais funcionalidades"
        />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="flex justify-center">
            <IconButton
              icon={Activity}
              onClick={() => navigate('/movimentacao')}
              variant="primary"
              size="lg"
              title="Movimentação — Gerenciar registros"
            />
          </div>

          <div className="flex justify-center">
            <IconButton
              icon={Archive}
              onClick={() => navigate('/consolidacao')}
              variant="purple"
              size="lg"
              title="Consolidação — Validar e exportar"
            />
          </div>

          <div className="flex justify-center">
            <IconButton
              icon={Landmark}
              onClick={() => navigate('/cliente')}
              variant="purple"
              size="lg"
              title="Clientes — Seguradoras"
            />
          </div>

          <div className="flex justify-center">
            <IconButton
              icon={Layers}
              onClick={() => navigate('/faixas')}
              variant="info"
              size="lg"
              title="Faixas — Classes de valor"
            />
          </div>

          <div className="flex justify-center">
            <IconButton
              icon={Shield}
              onClick={() => navigate('/gruposegur')}
              variant="success"
              size="lg"
              title="Grupos — Grupos Segur"
            />
          </div>

          <div className="flex justify-center">
            <IconButton
              icon={Gavel}
              onClick={() => navigate('/regras')}
              variant="warning"
              size="lg"
              title="Regras — Taxas e idades"
            />
          </div>
        </div>
      </div>

      {movimentacoes.length === 0 && consolidacoes.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-4">
          <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-amber-800">Nenhum dado carregado ainda</h4>
            <p className="text-sm text-amber-700 mt-1">
              O Dashboard mostra estatísticas reais de Movimentações e Consolidações. Comece cadastrando registros nas páginas acima.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
